import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardContent, Chip,
  Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton,
  MenuItem, Select, Slider, Tooltip, Typography,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import PeopleAltIcon       from "@mui/icons-material/PeopleAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import WarningAmberIcon    from "@mui/icons-material/WarningAmber";
import RefreshIcon         from "@mui/icons-material/Refresh";
import InfoOutlinedIcon    from "@mui/icons-material/InfoOutlined";
import SearchIcon          from "@mui/icons-material/Search";
import FilterListIcon      from "@mui/icons-material/FilterList";
import SyncIcon            from "@mui/icons-material/Sync";
import CloseIcon           from "@mui/icons-material/Close";
import CodeIcon            from "@mui/icons-material/Code";
import HandymanIcon        from "@mui/icons-material/Handyman";
import WorkHistoryIcon     from "@mui/icons-material/WorkHistory";
import LocationOnIcon      from "@mui/icons-material/LocationOn";
import InsightsIcon        from "@mui/icons-material/Insights";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { getTalentBoard } from "../../services/SearchService";
import Loader from "../../components/Loader/Loader";

const BLUE       = "#03407b";
const LIGHT_BLUE = "#1a7ac4";
const EXP_COLORS = ["#7fb3d3", "#03407b", "#02357a", "#012d6b"];
const REFRESH_MS = 60_000;

const pageSx = {
  minHeight: 'calc(100vh - 64px)',
  px: { xs: 1.5, md: 2.5 },
  py: 2,
  background:
    'linear-gradient(180deg, #f8fbff 0%, #eef4fa 46%, #f6f9fc 100%)',
};

const panelSx = {
  border: '1px solid #d9e6f2',
  borderRadius: '14px',
  background: 'rgba(255,255,255,0.94)',
  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 16px 34px rgba(15,23,42,0.06)',
};

const chartCardSx = {
  ...panelSx,
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  '& .MuiCardContent-root': {
    height: '100%',
  },
};

const baseTooltip = {
  trigger: 'axis',
  axisPointer: { type: 'shadow' },
  backgroundColor: 'rgba(15,23,42,0.94)',
  borderWidth: 0,
  textStyle: { color: '#fff', fontFamily: 'Fira Sans' },
  extraCssText: 'box-shadow:0 12px 32px rgba(15,23,42,0.24);border-radius:10px;',
};

const makeVerticalBarOption = ({ labels, values, color }) => ({
  animationDuration: 700,
  grid: { left: 46, right: 22, top: 26, bottom: 92 },
  tooltip: baseTooltip,
  xAxis: {
    type: 'category',
    data: labels,
    axisTick: { show: false },
    axisLine: { lineStyle: { color: '#d9e6f2' } },
    axisLabel: { color: '#64748b', fontSize: 11, rotate: 42, interval: Math.max(0, Math.floor(labels.length / 18)) },
  },
  yAxis: {
    type: 'value',
    splitLine: { lineStyle: { color: '#eef4fa' } },
    axisLabel: { color: '#64748b', fontSize: 11 },
  },
  series: [{
    name: 'Candidats',
    type: 'bar',
    data: values,
    barMaxWidth: 24,
    itemStyle: {
      color,
      borderRadius: [7, 7, 2, 2],
    },
    emphasis: {
      itemStyle: {
        shadowBlur: 12,
        shadowColor: `${color}55`,
      },
    },
  }],
});

const makeHorizontalBarOption = ({ labels, values, color }) => ({
  animationDuration: 700,
  grid: { left: 128, right: 44, top: 8, bottom: 24 },
  tooltip: baseTooltip,
  xAxis: {
    type: 'value',
    splitLine: { lineStyle: { color: '#eef4fa' } },
    axisLabel: { color: '#64748b', fontSize: 11 },
  },
  yAxis: {
    type: 'category',
    data: labels,
    axisTick: { show: false },
    axisLine: { lineStyle: { color: '#d9e6f2' } },
    axisLabel: { color: '#334155', fontSize: 12, width: 116, overflow: 'truncate' },
    inverse: true,
  },
  series: [{
    name: 'Candidats',
    type: 'bar',
    data: values,
    barMaxWidth: 18,
    label: { show: true, position: 'right', color: '#64748b', fontSize: 11, fontWeight: 600 },
    itemStyle: {
      color,
      borderRadius: [2, 8, 8, 2],
    },
  }],
});

const makeDonutOption = (slices) => ({
  animationDuration: 700,
  tooltip: {
    trigger: 'item',
    backgroundColor: 'rgba(15,23,42,0.94)',
    borderWidth: 0,
    textStyle: { color: '#fff', fontFamily: 'Fira Sans' },
    extraCssText: 'box-shadow:0 12px 32px rgba(15,23,42,0.24);border-radius:10px;',
  },
  series: [{
    type: 'pie',
    radius: ['52%', '78%'],
    center: ['50%', '50%'],
    avoidLabelOverlap: true,
    data: slices.map((s) => ({ name: s.label, value: s.value, itemStyle: { color: s.color } })),
    label: { color: '#475569', fontWeight: 700, formatter: '{b}' },
    labelLine: { lineStyle: { color: '#cbd5e1' } },
    itemStyle: {
      borderColor: '#fff',
      borderWidth: 3,
      borderRadius: 8,
    },
  }],
});


const _norm = (s) =>
  s.toLowerCase()
   .replace(/^[.\s]+/, "")
   .replace(/[.\s]/g, "");

const missionCovers = (tech = "", topLangs = [], topSkills = []) => {
  if (!tech.trim()) return true;
  const allKnown = [...topLangs, ...topSkills].map((t) => _norm(t.name));
  const words = tech.toLowerCase().split(/[\s,;/+&()\-]+/).filter((w) => w.length > 1);
  return words.some((w) => {
    const nw = _norm(w);
    return allKnown.some((k) => k.includes(nw) || nw.includes(k));
  });
};

const getMissionCount = (tech = "", topLangs = [], topSkills = []) => {
  if (!tech.trim()) return null;
  const words = tech.toLowerCase().split(/[\s,;/+&()\-]+/).filter((w) => w.length > 1);
  const all = [...topLangs, ...topSkills];
  let best = 0;
  for (const word of words) {
    const nw = _norm(word);
    for (const item of all) {
      const nk = _norm(item.name);
      if (nk.includes(nw) || nw.includes(nk)) {
        if (item.count > best) best = item.count;
      }
    }
  }
  return best > 0 ? best : null;
};


const TOP_N = 15;

// Bar chart of the most frequent items, Top 15 by default with a "Voir tout" toggle.
const RankingChart = ({ title, subtitle, items, color, onPick }) => {
  const [showAll, setShowAll] = React.useState(false);
  const list = showAll ? items : items.slice(0, TOP_N);
  const labels = list.map((d) => d.name);
  const values = list.map((d) => d.count);
  return (
    <Card elevation={0} sx={chartCardSx}>
      <CardContent sx={{ p: '18px !important' }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
          <Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>{title}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</Typography>
          </Box>
          {items.length > TOP_N && (
            <Chip
              size="small"
              label={showAll ? `Top ${TOP_N}` : `Voir tout (${items.length})`}
              onClick={() => setShowAll((v) => !v)}
              sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem', color: BLUE, background: '#ebf4fb', '&:hover': { background: '#dbeefa' } }}
            />
          )}
        </Box>
        {labels.length > 0 ? (
          <ReactECharts
            option={makeHorizontalBarOption({ labels, values, color })}
            style={{ height: Math.max(220, labels.length * 26 + 40), width: '100%', cursor: 'pointer' }}
            notMerge
            lazyUpdate
            onEvents={{ click: (params) => labels[params.dataIndex] && onPick(labels[params.dataIndex]) }}
          />
        ) : <Typography color="text.secondary">Aucune donnée</Typography>}
      </CardContent>
    </Card>
  );
};

const SectionTitle = ({ children }) => (
  <Typography sx={{ fontSize: '1rem', fontWeight: 850, color: '#0f172a', mb: 2 }}>
    {children}
  </Typography>
);

const KpiCard = ({ label, value, icon, color = BLUE }) => (
  <Card elevation={0} sx={{
    ...panelSx,
    height: "100%",
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 16,
      bottom: 14,
      width: 72,
      height: 5,
      borderRadius: 99,
      background: `linear-gradient(90deg, ${color}44, ${color}11)`,
    },
  }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: '18px !important' }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: "14px",
        background: `linear-gradient(135deg, ${color}, ${color}aa)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", flexShrink: 0,
        boxShadow: `0 12px 22px ${color}30`,
      }}>
        {icon}
      </Box>
      <Box minWidth={0}>
        <Typography variant="h4" fontWeight={850} color={color} lineHeight={1}>{value}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 600 }}>{label}</Typography>
      </Box>
    </CardContent>
  </Card>
);


const CHART_DESCRIPTIONS = [
  {
    icon: <CodeIcon sx={{ color: BLUE, fontSize: 26 }} />,
    title: "Top langages & technologies",
    desc: "Les langages de programmation les plus présents dans votre vivier. Permet de savoir immédiatement sur quelles technologies vous êtes forts pour répondre à une mission.",
  },
  {
    icon: <HandymanIcon sx={{ color: BLUE, fontSize: 26 }} />,
    title: "Top compétences techniques",
    desc: "Les savoir-faire métier (Agile, Docker, UML…) les plus répandus parmi vos candidats. Utile pour évaluer la profondeur de votre vivier sur des compétences transverses.",
  },
  {
    icon: <WorkHistoryIcon sx={{ color: BLUE, fontSize: 26 }} />,
    title: "Répartition par expérience",
    desc: "La distribution de vos candidats par tranche d'années d'expérience. Montre si votre vivier est plutôt junior, mid-level ou senior — et donc quels types de missions vous pouvez couvrir.",
  },
  {
    icon: <LocationOnIcon sx={{ color: BLUE, fontSize: 26 }} />,
    title: "Distribution géographique",
    desc: "Les villes d'où proviennent le plus de candidats. Essentiel pour les missions avec contrainte de localisation ou de mobilité.",
  },
];

const InfoModal = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
    slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
    <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <InfoOutlinedIcon sx={{ color: BLUE }} />
        <Typography fontWeight={700} fontSize={18}>À quoi sert cette page ?</Typography>
      </Box>
      <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
    </DialogTitle>
    <Divider />
    <DialogContent sx={{ pt: 2.5 }}>
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#f0f9ff", border: `1px solid #bae6fd`, mb: 3 }}>
        <Typography variant="body2" lineHeight={1.9} color="text.secondary">
          Le <strong style={{ color: BLUE }}>Talent Intelligence Board</strong> vous donne une vision
          en temps réel de votre vivier de candidats. Sans export, sans tableur —
          vous savez en un coup d'œil <strong>ce que vous avez</strong>, <strong>qui est disponible</strong>,
          et <strong>pour quelles missions vous manquez de profils</strong>.
          C'est votre radar de sourcing : anticipez les gaps avant que le client ne demande.
        </Typography>
      </Box>

      <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={1.5}>
        Ce que représente chaque graphique
      </Typography>

      {CHART_DESCRIPTIONS.map((c, i) => (
        <Box key={i}>
          <Box display="flex" gap={1.5} py={1.5} alignItems="flex-start">
            <Box sx={{ mt: 0.25, flexShrink: 0 }}>{c.icon}</Box>
            <Box>
              <Typography variant="body2" fontWeight={700} color="text.primary" gutterBottom>
                {c.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                {c.desc}
              </Typography>
            </Box>
          </Box>
          {i < CHART_DESCRIPTIONS.length - 1 && <Divider />}
        </Box>
      ))}
    </DialogContent>
  </Dialog>
);

const MissionInfoModal = ({ open, onClose, gaps = [], covered = [] }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
    slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
    <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <WarningAmberIcon sx={{ color: "#d97706" }} />
        <Typography fontWeight={700} fontSize={18}>Missions actives & Alertes Gap</Typography>
      </Box>
      <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
    </DialogTitle>
    <Divider />
    <DialogContent sx={{ pt: 2.5 }}>

      <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#f0f9ff", border: "1px solid #bae6fd", mb: 2.5 }}>
        <Typography variant="body2" lineHeight={1.9} color="text.secondary">
          Cette section liste toutes les <strong>missions actives</strong> du CRM et indique
          pour chacune si votre vivier contient des profils correspondants.
        </Typography>
      </Box>

      <Box mb={2.5}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700} color="#15803d">Couvert</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
          Au moins un langage ou une compétence de la mission correspond à une technologie
          bien représentée dans votre vivier. Vous avez des candidats potentiels pour répondre
          à cette mission.
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box mb={2.5}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <WarningAmberIcon sx={{ color: "#d97706", fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700} color="#c2410c">Gap</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
          Aucun profil correspondant à la technologie requise n'est détecté dans votre vivier.
          C'est un <strong>signal d'alerte sourcing</strong> : vous avez une mission ouverte
          mais pas les candidats pour la pourvoir. Il faut aller chercher ces profils activement
          avant que le client ne relance.
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={1}>
          Comment passer de Gap à Couvert ?
        </Typography>
        <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
          1. Uploader des CVs avec la technologie manquante dans le CRM<br />
          2. L'IA extrait automatiquement les compétences<br />
          3. Au prochain rafraîchissement du board, la mission passe en <strong style={{ color: "#15803d" }}>Couvert</strong>
        </Typography>
      </Box>

      {gaps.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" fontWeight={700} color="#c2410c" mb={1}>
              {gaps.length} gap{gaps.length > 1 ? "s" : ""} actuellement détecté{gaps.length > 1 ? "s" : ""}
            </Typography>
            {gaps.map((m) => (
              <Box key={m.id} display="flex" alignItems="flex-start" gap={1} mb={0.75}>
                <WarningAmberIcon sx={{ fontSize: 15, color: "#d97706", mt: "3px", flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary">
                  <strong>{m.title}</strong>{m.tech ? ` — ${m.tech}` : ""}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </DialogContent>
  </Dialog>
);


export default function TalentBoard() {
  const history = useHistory();

  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const [lastUpdated,setLastUpdated]= useState(null);
  const [infoOpen,       setInfoOpen]       = useState(false);
  const [missionInfoOpen,setMissionInfoOpen] = useState(false);
  const [location, setLocation] = useState("");
  const [expRange, setExpRange] = useState([0, 30]);

  const [pendingExp, setPendingExp] = useState([0, 30]);

  const timerRef = useRef(null);

  const load = useCallback((isBackground = false) => {
    if (!isBackground) setLoading(true);
    else setRefreshing(true);

    const params = new URLSearchParams();
    if (location)        params.set("location", location);
    if (expRange[0] > 0) params.set("min_exp", expRange[0]);
    if (expRange[1] < 30)params.set("max_exp", expRange[1]);

    getTalentBoard(params.toString())
      .then((res) => { setData(res.data); setLastUpdated(new Date()); setError(null); })
      .catch((err) => setError(err?.response?.data?.detail || "Erreur de chargement"))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [location, expRange]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    timerRef.current = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const applyExpFilter = () => setExpRange(pendingExp);

  const resetFilters = () => {
    setLocation("");
    setExpRange([0, 30]);
    setPendingExp([0, 30]);
  };

  const navigateToSearch = (query) => {
    window.dispatchEvent(new CustomEvent("pia:autosend", { detail: { message: query } }));
  };

  const hasActiveFilters = location || expRange[0] > 0 || expRange[1] < 30;
  const expPending = pendingExp[0] !== expRange[0] || pendingExp[1] !== expRange[1];

  if (loading) {
    return (
      <Box sx={{ ...pageSx, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          flex: 1,
          minHeight: 420,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...panelSx,
        }}>
          <Loader />
        </Box>
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={pageSx}>
        <Typography sx={{ pt: 2, pb: 1.5, color: '#0f172a', fontFamily: "'Fira Sans', sans-serif", fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em' }}>
          TALENT INTELLIGENCE BOARD
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const topLangs  = data?.top_languages || [];
  const topSkills = data?.top_skills    || [];
  const langNames  = topLangs.map((d) => d.name);
  const langCounts = topLangs.map((d) => d.count);
  const skillNames = topSkills.map((d) => d.name);
  const skillCounts= topSkills.map((d) => d.count);
  const locNames   = (data?.top_locations || []).map((d) => d.name);
  const locCounts  = (data?.top_locations || []).map((d) => d.count);

  const expDist   = data?.experience_distribution || {};
  const expSlices = [
    { label: "0-2 ans",  value: expDist["0-2"]  || 0, color: EXP_COLORS[0] },
    { label: "2-5 ans",  value: expDist["2-5"]  || 0, color: EXP_COLORS[1] },
    { label: "5-10 ans", value: expDist["5-10"] || 0, color: EXP_COLORS[2] },
    { label: "10+ ans",  value: expDist["10+"]  || 0, color: EXP_COLORS[3] },
  ].filter((s) => s.value > 0);

  const avail    = data?.availability    || {};
  const missions = data?.active_missions || [];
  const techMissions   = missions.filter((m) => m.tech && m.tech.trim());
  const gaps     = techMissions.filter((m) => !missionCovers(m.tech, topLangs, topSkills));
  const covered  = techMissions.filter((m) =>  missionCovers(m.tech, topLangs, topSkills));

  return (
    <Box sx={pageSx}>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ebf4fb', color: BLUE }}>
            <InsightsIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>Talent board</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>Forces du vivier, disponibilités et missions non couvertes en un coup d'œil</Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1} sx={{ ml: { md: 'auto' } }}>
          {refreshing && (
            <SyncIcon sx={{ color: BLUE, fontSize: 18, animation: "spin 1s linear infinite",
              "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } } }} />
          )}
          {lastUpdated && (
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
              Mis à jour à {lastUpdated.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </Typography>
          )}
          <Tooltip title="Rafraîchir maintenant">
            <IconButton size="small" onClick={() => load(false)}
              sx={{ color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fff', '&:hover': { background: '#f0f7fd', color: BLUE } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Comment lire ce tableau ?">
            <IconButton size="small" onClick={() => setInfoOpen(true)}
              sx={{ color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fff', '&:hover': { background: '#f0f7fd', color: BLUE } }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ ...panelSx, mb: 2, px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <LocationOnIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
          <Select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            displayEmpty size="small" sx={{ minWidth: 200, borderRadius: '10px', bgcolor: '#fff' }}
          >
            <MenuItem value="">Toutes les villes</MenuItem>
            {(data?.top_locations || []).map((l) => (
              <MenuItem key={l.name} value={l.name}>{l.name}</MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 320px', minWidth: 260 }}>
          <WorkHistoryIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', minWidth: 140 }}>
            Expérience : {pendingExp[0]} – {pendingExp[1] === 30 ? "30+" : pendingExp[1]} ans
          </Typography>
          <Slider
            value={pendingExp}
            onChange={(_, v) => setPendingExp(v)}
            onChangeCommitted={(_, v) => setExpRange(v)}
            min={0} max={30}
            valueLabelDisplay="auto"
            size="small"
            sx={{ color: BLUE, flex: 1 }}
          />
        </Box>

        {hasActiveFilters && (
          <Chip label="Réinitialiser" size="small" onDelete={resetFilters} onClick={resetFilters}
            sx={{ height: 28, fontWeight: 600, color: '#475569', background: '#f1f5f9' }} />
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard label="Candidats dans le vivier" value={data?.total ?? 0}
            icon={<PeopleAltIcon fontSize="small" />} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard label="Disponibles maintenant" value={avail.disponible ?? "—"}
            icon={<CheckCircleOutlineIcon fontSize="small" />} color="#16a34a" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <KpiCard label="Disponibles < 1 mois" value={avail.dispo_soon ?? "—"}
            icon={<WarningAmberIcon fontSize="small" />} color="#d97706" />
        </Box>
        <Box sx={{ minWidth: 0, cursor: 'pointer' }} onClick={() => setMissionInfoOpen(true)} title="Voir les missions non couvertes">
          <KpiCard label="Missions non couvertes (gap)" value={gaps.length}
            icon={<ReportProblemOutlinedIcon fontSize="small" />} color={gaps.length ? "#b91c1c" : "#16a34a"} />
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 2, mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <RankingChart
            title="Top langages & technologies"
            subtitle="Nombre de candidats par technologie · cliquez sur une barre pour chercher ces profils"
            items={topLangs} color={BLUE}
            onPick={(name) => navigateToSearch(`Trouve moi des candidats ${name}`)}
          />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <RankingChart
            title="Top compétences techniques"
            subtitle="Nombre de candidats par compétence · cliquez sur une barre pour chercher ces profils"
            items={topSkills} color={LIGHT_BLUE}
            onPick={(name) => navigateToSearch(`Trouve moi des candidats avec la competence ${name}`)}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 0.85fr) minmax(0, 1.15fr)' }, gap: 2, mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Card elevation={0} sx={chartCardSx}>
            <CardContent sx={{ p: '18px !important' }}>
              <SectionTitle>Répartition par expérience</SectionTitle>
              {expSlices.length > 0 ? (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <ReactECharts
                    option={makeDonutOption(expSlices)}
                    style={{ height: 300, width: '100%' }}
                    notMerge
                    lazyUpdate
                  />
                  <Box display="flex" flexWrap="wrap" gap={1.5} justifyContent="center" mt={1}>
                    {expSlices.map((s) => (
                      <Box key={s.label} display="flex" alignItems="center" gap={0.5}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: s.color }} />
                        <Typography variant="caption" color="text.secondary">
                          {s.label} — {s.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : <Typography color="text.secondary">Aucune donnée</Typography>}
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Card elevation={0} sx={chartCardSx}>
            <CardContent sx={{ p: '18px !important' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600}>Distribution géographique</Typography>
                <Typography variant="caption" color="text.disabled">Cliquez pour filtrer par ville</Typography>
              </Box>
              {locNames.length > 0 ? (
                <ReactECharts
                  option={makeHorizontalBarOption({ labels: locNames.slice(0, TOP_N), values: locCounts.slice(0, TOP_N), color: BLUE })}
                  style={{ height: Math.max(220, Math.min(locNames.length, TOP_N) * 26 + 40), width: '100%', cursor: 'pointer' }}
                  notMerge
                  lazyUpdate
                  onEvents={{
                    click: (params) => {
                      const city = locNames[params.dataIndex];
                      if (city) setLocation(city);
                    },
                  }}
                />
              ) : <Typography color="text.secondary">Aucune donnée</Typography>}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Card elevation={0} sx={{ ...panelSx, overflow: 'hidden' }}>
        <CardContent sx={{ p: '18px !important' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="h6" fontWeight={850} color="text.primary">
                Missions actives
              </Typography>
              {missions.length > 0 && (
                <Typography variant="caption" color="text.disabled" ml={0.5}>
                  ({missions.length} en cours)
                </Typography>
              )}
              <Tooltip title="Comprendre les alertes Gap">
                <IconButton size="small" onClick={() => setMissionInfoOpen(true)}
                  sx={{ color: "#94a3b8", "&:hover": { color: BLUE } }}>
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {gaps.length > 0 && (
              <Chip
                icon={<WarningAmberIcon fontSize="small" />}
                label={`${gaps.length} gap${gaps.length > 1 ? "s" : ""} détecté${gaps.length > 1 ? "s" : ""}`}
                size="small"
                onClick={() => setMissionInfoOpen(true)}
                sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 600, cursor: "pointer" }}
              />
            )}
          </Box>

          {missions.length === 0 ? (
            <Typography color="text.secondary" variant="body2">Aucune mission active</Typography>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 2,
            }}>
              {missions.map((m) => {
                const hasNoTech = !m.tech || !m.tech.trim();
                const covered   = !hasNoTech && missionCovers(m.tech, topLangs, topSkills);
                const count     = hasNoTech ? null : getMissionCount(m.tech, topLangs, topSkills);

                const borderColor = hasNoTech ? "#e5e7eb" : covered ? "#bbf7d0" : "#fed7aa";
                const bgColor     = hasNoTech ? "#f9fafb" : covered ? "#f0fdf4" : "#fff7ed";
                const chipBg      = hasNoTech ? "#f3f4f6" : covered ? "#dcfce7" : "#ffedd5";
                const chipColor   = hasNoTech ? "#6b7280" : covered ? "#15803d" : "#c2410c";
                const btnBg       = hasNoTech ? "#f3f4f6" : covered ? "#dcfce7" : "#ffedd5";
                const btnHoverBg  = hasNoTech ? "#e5e7eb" : covered ? "#bbf7d0" : "#fed7aa";
                const btnColor    = hasNoTech ? "#6b7280" : covered ? "#15803d" : "#c2410c";

                return (
                  <Box key={m.id} sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        p: 2, borderRadius: 2.5, border: "1px solid",
                        borderColor, bgcolor: bgColor,
                        minHeight: 184,
                        display: "flex", flexDirection: "column", gap: 0.75,
                        cursor: m.societe_id ? "pointer" : "default",
                        transition: "transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease",
                        boxShadow: "0 8px 18px rgba(15,23,42,0.04)",
                        "&:hover": {
                          transform: m.societe_id ? "translateY(-2px)" : "none",
                          boxShadow: m.societe_id ? "0 14px 30px rgba(15,23,42,0.12)" : "0 8px 18px rgba(15,23,42,0.04)",
                        },
                      }}
                      onClick={() => {
                        if (m.societe_id && m.id) {
                          history.push(`/societe/${m.societe_id}/missions/${m.id}/view`);
                        }
                      }}
                      title={m.societe_id ? "Voir la mission" : ""}
                    >
                      <Box display="flex" alignItems="center" gap={0.75}>
                        {hasNoTech
                          ? <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af", flexShrink: 0 }} />
                          : covered
                            ? <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#16a34a", flexShrink: 0 }} />
                            : <WarningAmberIcon sx={{ fontSize: 16, color: "#d97706", flexShrink: 0 }} />
                        }
                        <Typography variant="body2" fontWeight={850} noWrap title={m.title} flex={1} sx={{ fontSize: '1rem' }}>
                          {m.title || "Mission sans titre"}
                        </Typography>
                      </Box>

                      {hasNoTech ? (
                        <Box sx={{
                          display: "flex", alignItems: "flex-start", gap: 0.75,
                          p: "6px 8px", borderRadius: 1,
                          bgcolor: "#fefce8", border: "1px dashed #fde68a",
                        }}>
                          <WarningAmberIcon sx={{ fontSize: 13, color: "#ca8a04", mt: "1px", flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ color: "#92400e", lineHeight: 1.4 }}>
                            Aucune compétence technique renseignée.{" "}
                            <span
                              style={{ textDecoration: "underline", cursor: "pointer" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (m.societe_id && m.id) {
                                  history.push(`/societe/${m.societe_id}/missions/${m.id}/update`);
                                }
                              }}
                            >
                              Compléter la mission
                            </span>
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary" noWrap title={m.tech}>
                          {m.tech}
                        </Typography>
                      )}

                      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mt: 'auto' }}>
                        <Chip
                          label={hasNoTech ? "Non analysée" : covered ? "Couvert" : "Gap"}
                          size="small"
                          sx={{
                            fontSize: 10, height: 20,
                            bgcolor: chipBg, color: chipColor, fontWeight: 850,
                            border: `1px solid ${borderColor}`,
                          }}
                        />
                        {count !== null && (
                          <Typography variant="caption" color="text.disabled">
                            ~{count} profils vivier
                          </Typography>
                        )}
                      </Box>

                      <Button
                        size="small"
                        startIcon={<SearchIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          const msg = m.tech
                            ? `Recherche des candidats pour la mission "${m.title}". Technologies requises : ${m.tech}. Trouve les meilleurs profils disponibles dans le vivier.`
                            : `Recherche des candidats pour la mission "${m.title}". Trouve les meilleurs profils disponibles dans le vivier.`;
                          navigateToSearch(msg);
                        }}
                        sx={{
                          mt: 0.5, alignSelf: "flex-start",
                          fontSize: 11, textTransform: "none", fontWeight: 800,
                          color: btnColor, p: "3px 9px", borderRadius: 1.25,
                          bgcolor: btnBg,
                          "&:hover": { bgcolor: btnHoverBg },
                          minWidth: 0,
                        }}
                      >
                        Rechercher des profils
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      <InfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />
      <MissionInfoModal
        open={missionInfoOpen}
        onClose={() => setMissionInfoOpen(false)}
        gaps={gaps}
        covered={covered}
      />
    </Box>
  );
}
